import { useEffect, useState } from 'react'
import { User } from 'lucide-react'
import { cacheImage, getCachedImage } from '../lib/imageCache'
import logger from '../lib/logger'

interface CachedAvatarProps {
  avatarUrl?: string
  userName: string
  userIdentifier: string // userId or email
  size?: 'sm' | 'md' | 'lg' // 40px, 48px, 96px
  className?: string
}

export default function CachedAvatar({
  avatarUrl,
  userName,
  userIdentifier,
  size = 'md',
  className = '',
}: CachedAvatarProps) {
  const [imageSrc, setImageSrc] = useState<string | null>(null)
  const [imageError, setImageError] = useState(false)

  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-12 w-12',
  }

  const iconSizes = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
  }

  useEffect(() => {
    if (!avatarUrl) {
      setImageSrc(null)
      setImageError(false)
      return
    }

    let isMounted = true

    async function loadImage() {
      try {
        // Try to get from cache first
        const cached = getCachedImage(userIdentifier, avatarUrl)

        if (cached && isMounted) {
          logger.debug('Using cached avatar for:', userIdentifier)
          setImageSrc(cached)
          setImageError(false)
          return
        }

        // Cache miss or expired, try to load from URL
        logger.debug('Cache miss, loading avatar from URL:', avatarUrl)

        // Create an image element to test if the URL is loadable
        const img = new Image()

        img.onload = async () => {
          if (!isMounted) return

          // Image loaded successfully, cache it
          logger.debug('Avatar loaded successfully, caching...')

          try {
            const cached = await cacheImage(userIdentifier, avatarUrl!)
            if (cached && isMounted) {
              setImageSrc(cached)
            } else if (isMounted) {
              // Caching failed, but we can still use the original URL
              setImageSrc(avatarUrl!)
            }
            setImageError(false)
          } catch (error) {
            logger.error('Failed to cache avatar:', error)
            // Use original URL as fallback
            if (isMounted) {
              setImageSrc(avatarUrl!)
              setImageError(false)
            }
          }
        }

        img.onerror = () => {
          if (!isMounted) return

          logger.warn('Failed to load avatar from URL:', avatarUrl)
          setImageSrc(null)
          setImageError(true)
        }

        // Set crossOrigin before setting src to handle CORS
        img.crossOrigin = 'anonymous'
        img.src = avatarUrl!
      } catch (error) {
        logger.error('Error loading avatar:', error)
        if (isMounted) {
          setImageSrc(null)
          setImageError(true)
        }
      }
    }

    loadImage()

    return () => {
      isMounted = false
    }
  }, [avatarUrl, userIdentifier])

  // Show default avatar if no image or error
  if (!imageSrc || imageError) {
    return (
      <div
        className={`${sizeClasses[size]} ${className} flex items-center justify-center rounded-xl bg-gradient-to-br from-emerald-200 via-sky-200 to-amber-200`}
      >
        <User className={`${iconSizes[size]} text-gray-700`} />
      </div>
    )
  }

  return (
    <img
      src={imageSrc}
      alt={userName}
      className={`${sizeClasses[size]} ${className} rounded-xl object-cover`}
      onError={() => {
        logger.warn('Image render error, showing default avatar')
        setImageError(true)
      }}
    />
  )
}
